/**
 * VM detail page component for viewing detailed information about a specific VM.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import {
  getVMDetails,
  refreshVMDetails,
  OpenStackVMDetail,
  getProject,
  OpenStackProject,
  getCloud,
  OpenStackCloud,
  VMIPAddress,
  VMSecurityGroupRule,
} from '../api/openstack';
import { RefreshIcon, PlayIcon, StopIcon, PauseIcon, TrashIcon, ClipboardIcon } from '../components/Icons';
import { useWebSocket } from '../contexts/WebSocketContext';
import { logError } from '../utils/errorLogging';
import { getErrorWithContext } from '../utils/errorMessages';
import './VMDetailPage.css';

export const VMDetailPage: React.FC = () => {
  const { projectId, vmId } = useParams<{ projectId: string; vmId: string }>();
  const navigate = useNavigate();
  const [vm, setVm] = useState<OpenStackVMDetail | null>(null);
  const [project, setProject] = useState<OpenStackProject | null>(null);
  const [cloud, setCloud] = useState<OpenStackCloud | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Sorting state for each table
  const [networksSort, setNetworksSort] = useState<{ column: string; direction: 'asc' | 'desc' }>({ column: '', direction: 'asc' });
  const [securityGroupsSort, setSecurityGroupsSort] = useState<{ column: string; direction: 'asc' | 'desc' }>({ column: '', direction: 'asc' });
  const [disksSort, setDisksSort] = useState<{ column: string; direction: 'asc' | 'desc' }>({ column: '', direction: 'asc' });
  const [metadataSort, setMetadataSort] = useState<{ column: string; direction: 'asc' | 'desc' }>({ column: '', direction: 'asc' });
  
  const { isConnected, on, off } = useWebSocket();

  const loadVMDetails = async () => {
    if (!vmId) return;
    
    try {
      setError(null);
      const vmData = await getVMDetails(vmId);
      setVm(vmData);
    } catch (err: any) {
      logError('Failed to load VM details', err);
      setError(getErrorWithContext(err, 'load VM details'));
    } finally {
      setLoading(false);
    }
  };

  const loadProjectDetails = async () => {
    if (!projectId) return;
    
    try {
      const projectData = await getProject(projectId);
      setProject(projectData);
      
      // Load cloud information to get cloud_name for breadcrumb
      if (projectData.cloud_id) {
        try {
          const cloudData = await getCloud(projectData.cloud_id);
          setCloud(cloudData);
        } catch (err: any) {
          logError('Failed to load cloud', err);
          // Don't fail the whole page if cloud load fails
        }
      }
    } catch (err: any) {
      logError('Failed to load project', err);
      // Don't fail the whole page if project load fails
    }
  };

  const handleRefresh = async () => {
    if (!vmId) return;
    
    setRefreshing(true);
    setError(null);
    setSuccess(null);
    
    try {
      await refreshVMDetails(vmId);
      await loadVMDetails();
      setSuccess('VM details refreshed successfully');
    } catch (err: any) {
      logError('Failed to refresh', err);
      setError(getErrorWithContext(err, 'refresh VM details'));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (vmId) {
      setLoading(true);
      loadVMDetails();
    }
    if (projectId) {
      loadProjectDetails();
    }
  }, [vmId, projectId]);

  // Register WebSocket event handlers for VM detail updates
  useEffect(() => {
    if (!vmId || !projectId) return;

    const handleVMListUpdate = async (message: { event: string; data?: any }) => {
      const { project_id, vm_data } = message.data || {};
      
      // If this project's VM list was updated (full refresh)
      if (project_id === projectId) {
        // Check if the current VM is still in the list
        if (vm_data && Array.isArray(vm_data)) {
          const vmStillExists = vm_data.some((vm: any) => vm.vm_id === vmId);
          
          if (!vmStillExists) {
            // VM was deleted - navigate back to project detail page
            navigate(`/projects/${projectId}`);
            return;
          }
        }
        
        // VM still exists - reload VM details since they may have been refreshed
        await loadVMDetails();
      }
    };

    const handleVMUpdate = async (message: { event: string; data?: any }) => {
      const { event_type, project_id, vm_data } = message.data || {};
      
      // If this project's VMs were updated (individual update)
      if (project_id === projectId && event_type === 'updated') {
        // If VM data is included in the message, try to find and update basic fields
        if (vm_data && Array.isArray(vm_data) && vmId) {
          const updatedVM = vm_data.find((vm: any) => vm.vm_id === vmId);
          if (updatedVM && vm) {
            // Update basic VM fields from the message data
            // Note: This doesn't include detail data (IPs, ports, etc.), so we still need to reload
            setVm({
              ...vm,
              ...updatedVM,
              // Preserve detail fields that aren't in the list data
              ip_addresses: vm.ip_addresses,
              ports: vm.ports,
              disks: vm.disks,
              security_groups: vm.security_groups,
              security_group_rules: vm.security_group_rules,
              availability_zone: updatedVM.availability_zone || vm.availability_zone,
              host: updatedVM.host || vm.host,
              hypervisor_hostname: updatedVM.hypervisor_hostname || vm.hypervisor_hostname,
              power_state: updatedVM.power_state || vm.power_state,
              task_state: updatedVM.task_state || vm.task_state,
              vm_state: updatedVM.vm_state || vm.vm_state,
              metadata: vm.metadata,
            });
          }
        }
        
        // Always reload VM details to get the latest detail data (IPs, ports, disks, etc.)
        await loadVMDetails();
      }
    };

    const unsubscribeVMList = on('vm_list_update', handleVMListUpdate);
    const unsubscribeVM = on('vm_update', handleVMUpdate);

    return () => {
      unsubscribeVMList();
      unsubscribeVM();
    };
  }, [vmId, projectId, on, off]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatBytes = (mb: number): string => {
    if (mb < 1024) {
      return `${mb} MB`;
    }
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatRelativeTime = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    
    // Parse the UTC date string and convert to local time
    let dateStr = dateString.trim();
    
    // Check if the string has timezone information (Z, +HH:MM, or -HH:MM)
    const hasTimezone = dateStr.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateStr);
    
    if (!hasTimezone) {
      // No timezone info - assume UTC and append 'Z'
      dateStr = dateStr.replace(/\s+$/, '');
      if (dateStr.includes('T')) {
        dateStr = dateStr + 'Z';
      } else {
        dateStr = dateStr + 'T00:00:00Z';
      }
    }
    
    const date = new Date(dateStr);
    
    // Verify the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSeconds < 60) {
      return `${diffSeconds} second${diffSeconds !== 1 ? 's' : ''} ago`;
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      // For longer periods, show the date
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const getStatusColor = (status?: string): string => {
    if (!status) return 'var(--text-secondary)';
    const upperStatus = status.toUpperCase();
    if (upperStatus === 'ACTIVE') return 'var(--success-color)';
    if (upperStatus === 'SHUTOFF' || upperStatus === 'STOPPED') return 'var(--text-secondary)';
    if (upperStatus === 'PAUSED' || upperStatus === 'SUSPENDED') return 'var(--warning-color)';
    if (upperStatus === 'ERROR' || upperStatus === 'FAILED') return 'var(--error-color)';
    return 'var(--text-color)';
  };

  const shouldShowStartButton = (status?: string): boolean => {
    if (!status) return true;
    const upperStatus = status.toUpperCase();
    // Show start button if VM is not active (shutoff, stopped, paused, etc.)
    return upperStatus !== 'ACTIVE';
  };

  const shouldShowStopPauseButtons = (status?: string): boolean => {
    if (!status) return false;
    const upperStatus = status.toUpperCase();
    // Show stop/pause buttons only if VM is active
    return upperStatus === 'ACTIVE';
  };

  // Consolidate floating IPs with their associated fixed IPs
  const consolidateIPs = (ipAddresses: VMIPAddress[] | undefined) => {
    if (!ipAddresses || ipAddresses.length === 0) return [];
    
    // Group IPs by network and version
    const grouped: Record<string, { fixed?: VMIPAddress, floating?: VMIPAddress }> = {};
    
    ipAddresses.forEach((ip) => {
      const key = `${ip.network}_v${ip.version}`;
      if (!grouped[key]) {
        grouped[key] = {};
      }
      
      if (ip.type === 'fixed') {
        grouped[key].fixed = ip;
      } else if (ip.type === 'floating') {
        grouped[key].floating = ip;
      }
    });
    
    // Convert to array of consolidated entries
    const consolidated: Array<{
      network: string;
      version: number;
      fixed?: VMIPAddress;
      floating?: VMIPAddress;
    }> = [];
    
    Object.entries(grouped).forEach(([key, ips]) => {
      const [network] = key.split('_v');
      const version = parseInt(key.split('_v')[1]);
      consolidated.push({
        network,
        version,
        fixed: ips.fixed,
        floating: ips.floating,
      });
    });
    
    return consolidated;
  };

  // Create Networks table consolidating ports and IP addresses
  const createNetworksTable = () => {
    if (!vm || !vm.ports || vm.ports.length === 0) return [];
    
    const networksTable: Array<{
      mac: string;
      fixed: string;
      floating: string;
      network: string;
      status: string;
    }> = [];
    
    // Get consolidated IPs by network
    const consolidatedIPs = consolidateIPs(vm.ip_addresses);
    const ipByNetwork = new Map<string, { fixed?: VMIPAddress; floating?: VMIPAddress }>();
    consolidatedIPs.forEach((ipGroup) => {
      ipByNetwork.set(ipGroup.network, { fixed: ipGroup.fixed, floating: ipGroup.floating });
    });
    
    // Create a map of IP address to network name for quick lookup
    const ipToNetwork = new Map<string, string>();
    vm.ip_addresses?.forEach((ip) => {
      ipToNetwork.set(ip.address, ip.network);
    });
    
    // Process each port
    vm.ports.forEach((port) => {
      let networkName = 'N/A';
      let fixedIP = 'N/A';
      let floatingIP = 'N/A';
      
      // Get fixed IP from port's fixed_ips
      if (port.fixed_ips && port.fixed_ips.length > 0) {
        fixedIP = port.fixed_ips[0].ip_address;
        // Find network name by matching the fixed IP
        networkName = ipToNetwork.get(fixedIP) || port.network_id || 'N/A';
        
        // Get floating IP for this network
        const networkIPs = ipByNetwork.get(networkName);
        if (networkIPs?.floating) {
          floatingIP = networkIPs.floating.address;
        }
      } else {
        // No fixed IPs in port, try to find network from network_id
        networkName = port.network_id || 'N/A';
        
        // Try to find IPs for this network (if network_id matches a network name)
        const networkIPs = ipByNetwork.get(networkName);
        if (networkIPs?.fixed) {
          fixedIP = networkIPs.fixed.address;
        }
        if (networkIPs?.floating) {
          floatingIP = networkIPs.floating.address;
        }
      }
      
      // If we still don't have a network name, try using port.network_id
      if (networkName === 'N/A' && port.network_id) {
        networkName = port.network_id;
        // Try to get IPs for this network one more time
        const networkIPs = ipByNetwork.get(networkName);
        if (!fixedIP && networkIPs?.fixed) {
          fixedIP = networkIPs.fixed.address;
        }
        if (!floatingIP && networkIPs?.floating) {
          floatingIP = networkIPs.floating.address;
        }
      }
      
      networksTable.push({
        mac: port.mac_address || 'N/A',
        fixed: fixedIP,
        floating: floatingIP,
        network: networkName,
        status: port.status || 'N/A',
      });
    });
    
    return networksTable;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess(`Copied "${text}" to clipboard`);
      setTimeout(() => setSuccess(null), 2000);
    }).catch(() => {
      setError('Failed to copy to clipboard');
    });
  };

  // Sort handlers for each table
  const handleNetworksSort = (column: string) => {
    setNetworksSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSecurityGroupsSort = (column: string) => {
    setSecurityGroupsSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDisksSort = (column: string) => {
    setDisksSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleMetadataSort = (column: string) => {
    setMetadataSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Generic sort function
  const sortTable = <T,>(data: T[], column: string, direction: 'asc' | 'desc', getValue: (item: T) => any): T[] => {
    return [...data].sort((a, b) => {
      const aValue = getValue(a);
      const bValue = getValue(b);
      
      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      
      // Handle strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return direction === 'asc' ? comparison : -comparison;
      }
      
      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Fallback to string comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      const comparison = aStr.localeCompare(bStr);
      return direction === 'asc' ? comparison : -comparison;
    });
  };

  // Deduplicate security group rules - if two rules match except for security group, keep only one
  // Prefer rules from "default" security group, otherwise pick the first one
  const deduplicateSecurityGroupRules = (rules: VMSecurityGroupRule[]): VMSecurityGroupRule[] => {
    // Create a map to track unique rules (keyed by all fields except security_group)
    const ruleMap = new Map<string, VMSecurityGroupRule[]>();
    
    rules.forEach((rule) => {
      // Create a key from all fields except security_group_id and security_group_name
      const key = JSON.stringify({
        direction: rule.direction || '',
        ethertype: rule.ethertype || '',
        protocol: rule.protocol || '',
        port_range_min: rule.port_range_min ?? null,
        port_range_max: rule.port_range_max ?? null,
        remote_ip_prefix: rule.remote_ip_prefix || '',
        remote_group_id: rule.remote_group_id || '',
        remote_group_name: rule.remote_group_name || '',
        description: rule.description || '',
      });
      
      if (!ruleMap.has(key)) {
        ruleMap.set(key, []);
      }
      ruleMap.get(key)!.push(rule);
    });
    
    // For each group of duplicate rules, pick the best one
    const deduplicated: VMSecurityGroupRule[] = [];
    
    ruleMap.forEach((duplicateRules) => {
      if (duplicateRules.length === 1) {
        // No duplicates, just add it
        deduplicated.push(duplicateRules[0]);
      } else {
        // Find the one from "default" security group (case-insensitive)
        const defaultRule = duplicateRules.find(rule => {
          const sgName = (rule.security_group_name || '').toLowerCase();
          return sgName === 'default';
        });
        
        if (defaultRule) {
          deduplicated.push(defaultRule);
        } else {
          // No default found, just pick the first one
          deduplicated.push(duplicateRules[0]);
        }
      }
    });
    
    return deduplicated;
  };

  if (loading) {
    return (
      <Layout>
        <div className="vm-detail-page">
          <div className="vm-detail-container">
            <div className="loading">Loading VM details...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !vm) {
    return (
      <Layout>
        <div className="vm-detail-page">
          <div className="vm-detail-container">
            <div className="vm-breadcrumb">
            <Link to="/projects">Projects</Link>
            {projectId && (
              <>
                <span className="vm-breadcrumb-separator">/</span>
                <Link to={`/projects/${projectId}`}>
                  {cloud?.cloud_name || project?.project_name || projectId}
                </Link>
              </>
            )}
            {vmId && (
              <>
                <span className="vm-breadcrumb-separator">/</span>
                <span className="vm-breadcrumb-current">VM Details</span>
              </>
            )}
            </div>
            <div className="error-message"><strong>Error:</strong> {error}</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="vm-detail-page">
        <div className="vm-detail-container">
          <div className="vm-breadcrumb">
          <Link to="/projects">Projects</Link>
          {projectId && (
            <>
              <span className="vm-breadcrumb-separator">/</span>
              <Link to={`/projects/${projectId}`}>
                {cloud?.cloud_name || project?.project_name || projectId}
              </Link>
            </>
          )}
          {vm && (
            <>
              <span className="vm-breadcrumb-separator">/</span>
              <span className="vm-breadcrumb-current">
                {vm.vm_name || vm.vm_id || 'VM Details'}
              </span>
            </>
          )}
        </div>
        <div className="vm-detail-header">
          <div className="vm-detail-title">
            <h1>{vm?.vm_name || vm?.vm_id || 'VM Details'}</h1>
          </div>
          <div className="vm-detail-actions">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="btn-icon"
              title="Refresh VM details"
              aria-label="Refresh VM details"
            >
              <RefreshIcon />
            </button>
            {vm && shouldShowStartButton(vm.status) && (
              <button
                className="btn-icon"
                disabled
                title="Start VM (coming soon)"
                aria-label="Start VM"
              >
                <PlayIcon />
              </button>
            )}
            {vm && shouldShowStopPauseButtons(vm.status) && (
              <>
                <button
                  className="btn-icon"
                  disabled
                  title="Stop VM (coming soon)"
                  aria-label="Stop VM"
                >
                  <StopIcon />
                </button>
                <button
                  className="btn-icon"
                  disabled
                  title="Pause VM (coming soon)"
                  aria-label="Pause VM"
                >
                  <PauseIcon />
                </button>
              </>
            )}
            <button
              className="btn-icon btn-delete"
              disabled
              title="Delete VM (coming soon)"
              aria-label="Delete VM"
            >
              <TrashIcon />
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            <strong>Success:</strong> {success}
          </div>
        )}

        {vm && (
          <>
            {/* Basic Information Section */}
            <div className="card vm-section">
              <h3>Basic Information</h3>
              <div className="vm-info-grid">
                <div className="vm-info-item">
                  <span className="vm-info-label">VM ID:</span>
                  <span className="vm-info-value vm-id-with-copy">
                    {vm.vm_id ? `${vm.vm_id.substring(0, 10)}...` : 'N/A'}
                    {vm.vm_id && (
                      <button
                        className="btn-copy-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(vm.vm_id);
                        }}
                        title="Copy full VM ID to clipboard"
                        aria-label="Copy VM ID"
                      >
                        <ClipboardIcon />
                      </button>
                    )}
                  </span>
                </div>
                <div className="vm-info-item">
                  <span className="vm-info-label">Status:</span>
                  <span className="vm-info-value" style={{ color: getStatusColor(vm.status) }}>
                    {vm.status || 'UNKNOWN'}
                  </span>
                </div>
                <div className="vm-info-item">
                  <span className="vm-info-label">Created By:</span>
                  <span className="vm-info-value">{vm.created_by_user_name || 'N/A'}</span>
                </div>
                <div className="vm-info-item">
                  <span className="vm-info-label">Created At:</span>
                  <span className="vm-info-value">{formatDate(vm.created_at)}</span>
                </div>
                <div className="vm-info-item">
                  <span className="vm-info-label">Flavor:</span>
                  <span className="vm-info-value">{vm.flavor_name || vm.flavor_id || 'N/A'}</span>
                </div>
                <div className="vm-info-item">
                  <span className="vm-info-label">Cores:</span>
                  <span className="vm-info-value">{vm.cores ?? 'N/A'}</span>
                </div>
                <div className="vm-info-item">
                  <span className="vm-info-label">RAM:</span>
                  <span className="vm-info-value">{vm.ram_mb ? formatBytes(vm.ram_mb) : 'N/A'}</span>
                </div>
                <div className="vm-info-item">
                  <span className="vm-info-label">Disk:</span>
                  <span className="vm-info-value">{vm.disk_gb ? `${vm.disk_gb} GB` : 'N/A'}</span>
                </div>
                <div className="vm-info-item">
                  <span className="vm-info-label">Image:</span>
                  <span className="vm-info-value">{vm.image_name || vm.image_id || 'N/A'}</span>
                </div>
                <div className="vm-info-item">
                  <span className="vm-info-label">Key Name:</span>
                  <span className="vm-info-value">{vm.key_name || 'N/A'}</span>
                </div>
                {vm.availability_zone && (
                  <div className="vm-info-item">
                    <span className="vm-info-label">Availability Zone:</span>
                    <span className="vm-info-value">{vm.availability_zone}</span>
                  </div>
                )}
                {vm.host && (
                  <div className="vm-info-item">
                    <span className="vm-info-label">Host:</span>
                    <span className="vm-info-value">{vm.host}</span>
                  </div>
                )}
                {vm.hypervisor_hostname && (
                  <div className="vm-info-item">
                    <span className="vm-info-label">Hypervisor:</span>
                    <span className="vm-info-value">{vm.hypervisor_hostname}</span>
                  </div>
                )}
                {vm.task_state && (
                  <div className="vm-info-item">
                    <span className="vm-info-label">Task State:</span>
                    <span className="vm-info-value">{vm.task_state}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="vm-last-refreshed">
              Last refreshed: {formatRelativeTime(vm.last_refreshed)}
            </div>

            {/* Networks Section */}
            {vm.ports && vm.ports.length > 0 && (() => {
              const networksTable = createNetworksTable();
              const sortedNetworks = networksSort.column
                ? sortTable(networksTable, networksSort.column, networksSort.direction, (row) => {
                    switch (networksSort.column) {
                      case 'mac': return row.mac;
                      case 'fixed': return row.fixed;
                      case 'floating': return row.floating;
                      case 'network': return row.network;
                      case 'status': return row.status;
                      default: return '';
                    }
                  })
                : networksTable;
              
              const SortableHeader = ({ column, label }: { column: string; label: string }) => (
                <th
                  className="sortable"
                  onClick={() => handleNetworksSort(column)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNetworksSort(column);
                    }
                  }}
                  aria-label={`Sort by ${label}`}
                >
                  {label}
                  {networksSort.column === column && (
                    <span className="sort-indicator">
                      {networksSort.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
              );
              
              return (
                <div className="card vm-section">
                  <h3>Networks</h3>
                  <table className="data-table">
                      <thead>
                        <tr>
                          <SortableHeader column="mac" label="MAC" />
                          <SortableHeader column="fixed" label="Fixed" />
                          <SortableHeader column="floating" label="Floating" />
                          <SortableHeader column="network" label="Network" />
                          <SortableHeader column="status" label="Status" />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedNetworks.map((row, index) => (
                          <tr key={index}>
                            <td className="vm-table-cell-monospace">{row.mac}</td>
                            <td className="vm-table-cell-monospace">
                              {row.fixed !== 'N/A' ? (
                                <span className="vm-ip-with-copy">
                                  {row.fixed}
                                  <button
                                    className="btn-copy-icon-small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(row.fixed);
                                    }}
                                    title="Copy fixed IP address to clipboard"
                                    aria-label="Copy fixed IP"
                                  >
                                    <ClipboardIcon width={16} height={16} />
                                  </button>
                                </span>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td className="vm-table-cell-monospace">
                              {row.floating !== 'N/A' ? (
                                <span className="vm-ip-with-copy">
                                  {row.floating}
                                  <button
                                    className="btn-copy-icon-small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(row.floating);
                                    }}
                                    title="Copy floating IP address to clipboard"
                                    aria-label="Copy floating IP"
                                  >
                                    <ClipboardIcon width={16} height={16} />
                                  </button>
                                </span>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td>{row.network}</td>
                            <td>{row.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                </div>
              );
            })()}

            {/* Security Groups Section - moved between Networks and Disks */}
            <div className="card vm-section">
              <h3>Security Groups</h3>
              {vm.security_group_rules && vm.security_group_rules.length > 0 ? (() => {
                // Deduplicate rules first (prefer default security group)
                const deduplicatedRules = deduplicateSecurityGroupRules(vm.security_group_rules);
                
                const sortedSecurityGroups = securityGroupsSort.column
                  ? sortTable(deduplicatedRules, securityGroupsSort.column, securityGroupsSort.direction, (rule) => {
                      switch (securityGroupsSort.column) {
                        case 'direction': return rule.direction || '';
                        case 'ethertype': return rule.ethertype || '';
                        case 'protocol': return rule.protocol || '';
                        case 'port': {
                          const hasMin = rule.port_range_min != null; // checks for both null and undefined
                          const hasMax = rule.port_range_max != null; // checks for both null and undefined
                          const portRange = hasMin || hasMax
                            ? hasMin && hasMax && rule.port_range_min === rule.port_range_max
                              ? String(rule.port_range_min)
                              : `${hasMin ? rule.port_range_min : 'any'}-${hasMax ? rule.port_range_max : 'any'}`
                            : '-';
                          return portRange;
                        }
                        case 'local_ip': {
                          const localIP = rule.remote_group_id ? 'All' : (vm.ip_addresses?.find(ip => ip.type === 'fixed')?.address || 'All');
                          return localIP;
                        }
                        case 'remote_ip': return rule.remote_ip_prefix || '';
                        case 'remote_group': return rule.remote_group_name || rule.remote_group_id || '';
                        case 'security_group': return rule.security_group_name || rule.security_group_id || '';
                        default: return '';
                      }
                    })
                  : deduplicatedRules;
                
                const SortableHeader = ({ column, label }: { column: string; label: string }) => (
                  <th
                    className="sortable"
                    onClick={() => handleSecurityGroupsSort(column)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSecurityGroupsSort(column);
                      }
                    }}
                    aria-label={`Sort by ${label}`}
                  >
                    {label}
                    {securityGroupsSort.column === column && (
                      <span className="sort-indicator">
                        {securityGroupsSort.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                );
                
                return (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <SortableHeader column="direction" label="Direction" />
                        <SortableHeader column="ethertype" label="Type" />
                        <SortableHeader column="protocol" label="Protocol" />
                        <SortableHeader column="local_ip" label="Local IP" />
                        <SortableHeader column="port" label="Port" />
                        <SortableHeader column="remote_ip" label="Remote IP" />
                        <SortableHeader column="remote_group" label="Remote Security Group" />
                        <SortableHeader column="security_group" label="Security Group" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSecurityGroups.map((rule: VMSecurityGroupRule, index: number) => {
                        // Format port range - show "-" if both are null/undefined
                        const hasMin = rule.port_range_min != null; // checks for both null and undefined
                        const hasMax = rule.port_range_max != null; // checks for both null and undefined
                        const portRange = hasMin || hasMax
                          ? hasMin && hasMax && rule.port_range_min === rule.port_range_max
                            ? String(rule.port_range_min)
                            : `${hasMin ? rule.port_range_min : 'any'}-${hasMax ? rule.port_range_max : 'any'}`
                          : '-';
                        
                        // Local IP - check if there's a remote_group_id (rule applies to all IPs in that group)
                        // or if remote_ip_prefix is specified (rule applies to specific network)
                        const localIP = rule.remote_group_id ? 'All' : (vm.ip_addresses?.find(ip => ip.type === 'fixed')?.address || 'All');
                        
                        // Format values, replacing N/A with -
                        const formatValue = (value: string | undefined | null, defaultValue: string = '-'): string => {
                          if (!value || value === 'N/A') return defaultValue;
                          return value;
                        };
                        
                        return (
                          <tr key={rule.id || index} title={rule.description && rule.description.trim() ? rule.description : undefined}>
                            <td>{formatValue(rule.direction)}</td>
                            <td>{formatValue(rule.ethertype)}</td>
                            <td>{formatValue(rule.protocol, 'Any')}</td>
                            <td className="vm-table-cell-monospace">{formatValue(localIP)}</td>
                            <td>{portRange}</td>
                            <td className="vm-table-cell-monospace">{formatValue(rule.remote_ip_prefix)}</td>
                            <td>{formatValue(rule.remote_group_name || rule.remote_group_id)}</td>
                            <td>{formatValue(rule.security_group_name || rule.security_group_id)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })() : (
                <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  No security groups or rules found for this VM. Security groups may not be attached, or the detail cache may need to be refreshed.
                </p>
              )}
            </div>

            {/* Disks/Volumes Section */}
            {vm.disks && vm.disks.length > 0 && (() => {
              const sortedDisks = disksSort.column
                ? sortTable(vm.disks, disksSort.column, disksSort.direction, (disk) => {
                    switch (disksSort.column) {
                      case 'device': return disk.device || '';
                      case 'size': return disk.size_gb ?? 0;
                      case 'name': return disk.name || '';
                      case 'boot': return disk.boot_index !== undefined && disk.boot_index >= 0 ? 'Yes' : 'No';
                      default: return '';
                    }
                  })
                : vm.disks;
              
              const SortableHeader = ({ column, label }: { column: string; label: string }) => (
                <th
                  className="sortable"
                  onClick={() => handleDisksSort(column)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleDisksSort(column);
                    }
                  }}
                  aria-label={`Sort by ${label}`}
                >
                  {label}
                  {disksSort.column === column && (
                    <span className="sort-indicator">
                      {disksSort.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
              );
              
              return (
                <div className="card vm-section">
                  <h3>Attached Disks/Volumes</h3>
                  <table className="data-table">
                      <thead>
                        <tr>
                          <SortableHeader column="device" label="Device" />
                          <SortableHeader column="size" label="Size (GB)" />
                          <SortableHeader column="name" label="Name" />
                          <SortableHeader column="boot" label="Boot" />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedDisks.map((disk, index) => (
                          <tr key={disk.id || index}>
                            <td className="vm-table-cell-monospace">{disk.device || 'N/A'}</td>
                            <td>{disk.size_gb ?? 'N/A'}</td>
                            <td>{disk.name || 'N/A'}</td>
                            <td>{disk.boot_index !== undefined && disk.boot_index >= 0 ? 'Yes' : 'No'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
              );
            })()}

            {/* Metadata Section (Collapsible) */}
            {vm.metadata && Object.keys(vm.metadata).length > 0 && (() => {
              const metadataEntries = Object.entries(vm.metadata);
              const sortedMetadata = metadataSort.column
                ? sortTable(metadataEntries, metadataSort.column, metadataSort.direction, (entry) => {
                    switch (metadataSort.column) {
                      case 'key': return entry[0];
                      case 'value': return String(entry[1]);
                      default: return '';
                    }
                  })
                : metadataEntries;
              
              const SortableHeader = ({ column, label }: { column: string; label: string }) => (
                <th
                  className="sortable"
                  onClick={() => handleMetadataSort(column)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleMetadataSort(column);
                    }
                  }}
                  aria-label={`Sort by ${label}`}
                >
                  {label}
                  {metadataSort.column === column && (
                    <span className="sort-indicator">
                      {metadataSort.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
              );
              
              return (
                <div className="card vm-section">
                  <details className="vm-metadata-details">
                    <summary className="vm-metadata-summary">
                      <h3>Metadata</h3>
                    </summary>
                    <div className="vm-metadata-content">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <SortableHeader column="key" label="Key" />
                            <SortableHeader column="value" label="Value" />
                          </tr>
                        </thead>
                        <tbody>
                          {sortedMetadata.map(([key, value]) => (
                            <tr key={key}>
                              <td className="vm-table-cell-monospace">{key}</td>
                              <td>{String(value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
              );
            })()}

            <div className="vm-last-refreshed">
              Last refreshed: {formatRelativeTime(vm.last_refreshed)}
            </div>
          </>
        )}
        </div>
      </div>
    </Layout>
  );
};

